import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icons } from '../common';
import qcService from '../../services/qcService';

/**
 * QCArticlesList - Display all completed QC analyses
 * Searchable and filterable table of past analyses
 */
const QCArticlesList = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    journal: '',
    status: '',
    searchId: '',
  });

  useEffect(() => {
    fetchArticles();
  }, [filters.journal, filters.status]);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const response = await qcService.getAnalyses({
        journal: filters.journal || undefined,
        status: filters.status || undefined,
        limit: 100,
      });
      setArticles(response.data.analyses || []);
    } catch (error) {
      console.error('Failed to fetch articles:', error);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredArticles = articles.filter(a =>
    !filters.searchId || a.articleId?.includes(filters.searchId)
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'PASSED':
        return 'bg-green-100 text-green-700';
      case 'FAILED':
        return 'bg-red-100 text-red-700';
      case 'NEEDS_REVIEW':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-50">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-200/50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <button
            onClick={() => navigate('/qc/dashboard')}
            className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium mb-4 transition-colors"
          >
            <Icons.ChevronLeftIcon className="w-5 h-5" />
            Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Completed Articles</h1>
          <p className="text-gray-600 mt-2">Browse and review previously analyzed documents</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Filters */}
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-8 border border-gray-200/50">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input
              type="text"
              placeholder="Search by Article ID"
              value={filters.searchId}
              onChange={(e) => setFilters({ ...filters, searchId: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <select
              value={filters.journal}
              onChange={(e) => setFilters({ ...filters, journal: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Journals</option>
              <option value="IJA">IJA</option>
              <option value="AIAN">AIAN</option>
              <option value="JPGM">JPGM</option>
              <option value="IJEM">IJEM</option>
              <option value="IJO">IJO</option>
            </select>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Status</option>
              <option value="PASSED">Passed</option>
              <option value="NEEDS_REVIEW">Needs Review</option>
              <option value="FAILED">Failed</option>
            </select>
          </div>
        </div>

        {/* Articles Table */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading articles...</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200/50 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200/50">
                  <tr>
                    <th className="text-left px-6 py-4 font-semibold text-gray-700">Journal</th>
                    <th className="text-left px-6 py-4 font-semibold text-gray-700">File</th>
                    <th className="text-center px-6 py-4 font-semibold text-gray-700">Score</th>
                    <th className="text-center px-6 py-4 font-semibold text-gray-700">Status</th>
                    <th className="text-right px-6 py-4 font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredArticles.length > 0 ? (
                    filteredArticles.map((article) => (
                      <tr
                        key={article.id}
                        className="border-b border-gray-100/50 hover:bg-gray-50 transition-colors cursor-pointer"
                      >
                        <td className="px-6 py-4">
                          <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-lg text-sm font-medium">
                            {article.journalCode}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-900 font-medium truncate">
                          {article.uploadedFilename}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="font-bold text-indigo-600">{Math.round(article.overallScore)}%</span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium ${getStatusColor(article.overallStatus)}`}>
                            {article.overallStatus}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => navigate(`/qc/analysis/${article.id}`)}
                            className="text-indigo-600 hover:text-indigo-700 font-medium text-sm transition-colors"
                          >
                            View Report
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center text-gray-600">
                        No articles found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QCArticlesList;
